import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  withStyles,
  Typography,
  ListSubheader,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  Checkbox,
  Chip,
  Box,
} from '@material-ui/core';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { Query, Mutation } from 'react-apollo';
import gql from 'graphql-tag';
import { withSnackbar } from 'notistack';
import Autocomplete from '@material-ui/lab/Autocomplete';
import InventoryUserAutocomplete from '../common/InventoryUserAutocomplete';

const GET_FORM_DATA = gql`
  query GetQuickNotesFormData {
    getActivityTypes {
      id
      name
    }
    getQuickNoteCategories {
      id
      name
      description
    }
    active_deals_for_quick_notes {
      id
      name
      pipeline {
        id
        name
      }
      pipeline_stage {
        id
        name
      }
    }
  }
`;

const UPDATE_QUICK_NOTE = gql`
  mutation UpdateQuickNote(
    $id: ID!
    $inventory_user_id: ID!
    $comment: String!
    $time_spent: Int!
    $activity_type_id: ID!
    $category_ids: [ID!]
    $interaction_date: String!
    $planned: Boolean!
    $pipeline_card_id: ID
  ) {
    updateQuickNote(
      id: $id
      inventory_user_id: $inventory_user_id
      comment: $comment
      time_spent: $time_spent
      activity_type_id: $activity_type_id
      category_ids: $category_ids
      interaction_date: $interaction_date
      planned: $planned
      pipeline_card_id: $pipeline_card_id
    ) {
      id
      comment
      time_spent
      time_spent_formatted
      interaction_date
      created_at
      planned
      pipeline_card_id
      pipeline_card {
        id
        name
        pipeline {
          id
          name
        }
      }
      user {
        id
        name
        __typename
      }
      commentable {
        id
        name
        __typename
      }
      activityType {
        id
        name
        __typename
      }
      all_categories {
        id
        name
        __typename
      }
      is_quick_note
      __typename
    }
  }
`;

// Time spent options (15-minute increments from 15 minutes to 8 hours)
const TIME_SPENT_OPTIONS = [];
for (let i = 15; i <= 480; i += 15) {
  const hours = Math.floor(i / 60);
  const minutes = i % 60;
  let label;
  if (hours === 0) {
    label = `${minutes} minutes`;
  } else if (minutes === 0) {
    label = `${hours} hour${hours > 1 ? 's' : ''}`;
  } else {
    label = `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minutes`;
  }
  TIME_SPENT_OPTIONS.push({ value: i, label });
}

const styles = (theme) => ({
  dialogContent: {
    paddingTop: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
});

class EditQuickNoteDialog extends Component {
  constructor(props) {
    super(props);
    
    const { note } = props;
    
    this.state = {
      inventoryUser: note.commentable ? {
        id: note.commentable.id,
        name: note.commentable.name,
        label: note.commentable.name,
        value: note.commentable.id,
      } : null,
      categoryIds: note.all_categories ? note.all_categories.map(cat => cat.id) : [],
      activityTypeId: note.activityType?.id || '',
      interactionDate: note.interaction_date ? new Date(note.interaction_date) : new Date(),
      timeSpent: note.time_spent || '',
      comment: note.comment || '',
      planned: note.planned || false,
      selectedPipelineCard: note.pipeline_card ? {
        id: note.pipeline_card.id,
        name: note.pipeline_card.name,
        pipeline: note.pipeline_card.pipeline,
      } : null,
      errors: {},
    };
  }

  // Helper function to clean category names for display
  cleanCategoryName = (categoryName) => {
    if (!categoryName) return '';
    const cleaned = categoryName
      .replace('Practice Exchange   ', '')
      .replace('Succession Planning   ', '');
    
    // Capitalize 3-letter words (like rsp, erd, etc.)
    return cleaned.split(' ').map(word => 
      word.length === 3 ? word.toUpperCase() : word
    ).join(' ');
  };

  processCategories = (categories) => {
    if (!categories) return { practiceExchange: [], successionPlanning: [], other: [] };
    
    const processed = categories.map(category => {
      const cleaned = category.name
        .replace('Practice Exchange   ', '')
        .replace('Succession Planning   ', '');
      
      // Capitalize 3-letter words (like rsp, erd, etc.)
      const displayName = cleaned.split(' ').map(word => 
        word.length === 3 ? word.toUpperCase() : word
      ).join(' ');
      
      return {
        ...category,
        displayName,
        section: category.name.startsWith('Practice Exchange   ') ? 'Practice Exchange' :
                category.name.startsWith('Succession Planning   ') ? 'Succession Planning' : 'Other'
      };
    });
    
    return {
      practiceExchange: processed.filter(c => c.section === 'Practice Exchange'),
      successionPlanning: processed.filter(c => c.section === 'Succession Planning'),
      other: processed.filter(c => c.section === 'Other')
    };
  };

  handleInputChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value });
  };

  handleCategoryChange = (e) => {
    const value = e.target.value;
    // Ensure we always have an array
    this.setState({ categoryIds: typeof value === 'string' ? [value] : value });
  };
  
  handleDateChange = (date) => {
    this.setState({ interactionDate: date });
  };
  
  handleInventoryUserChange = (user) => {
    this.setState({ inventoryUser: user });
  };

  validate = () => {
    const newErrors = {};
    const { inventoryUser, categoryIds, activityTypeId, interactionDate, timeSpent, comment } = this.state;
    
    if (!inventoryUser) {
      newErrors.inventoryUser = 'Inventory User is required';
    }
    
    if (!categoryIds || categoryIds.length === 0) {
      newErrors.categoryIds = 'At least one category is required';
    }
    
    if (!activityTypeId) newErrors.activityTypeId = 'Activity Type is required';
    
    if (!interactionDate) {
      newErrors.interactionDate = 'Interaction Date is required';
    } else {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      if (interactionDate > today) {
        newErrors.interactionDate = 'Interaction date cannot be in the future';
      }
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      if (interactionDate < oneYearAgo) {
        newErrors.interactionDate = 'Interaction date cannot be more than 1 year ago';
      }
    }

    if (!timeSpent && timeSpent !== 0) {
      newErrors.timeSpent = 'Time spent is required';
    } else if (timeSpent <= 0) {
      newErrors.timeSpent = 'Time spent must be greater than 0';
    }

    if (!comment) {
      newErrors.comment = 'Comment is required';
    } else if (comment.length < 5) {
      newErrors.comment = 'Comment must be at least 5 characters long';
    } else if (comment.length > 2000) {
      newErrors.comment = 'Comment cannot exceed 2000 characters';
    }

    if (typeof this.state.planned !== 'boolean') {
      newErrors.planned = 'Please select planned or unplanned';
    }

    this.setState({ errors: newErrors });
    return Object.keys(newErrors).length === 0;
  };

  handleSubmit = async (updateMutation) => {
    const { note, onSuccess, onClose, enqueueSnackbar } = this.props;
    
    if (!this.validate()) return;

    const { inventoryUser, categoryIds, activityTypeId, interactionDate, timeSpent, comment, planned, selectedPipelineCard } = this.state;

    try {
      const result = await updateMutation({
        variables: {
          id: note.id,
          inventory_user_id: inventoryUser.id,
          comment: comment,
          time_spent: parseInt(timeSpent, 10),
          activity_type_id: activityTypeId,
          category_ids: categoryIds,
          interaction_date: interactionDate.toISOString().split('T')[0],
          planned: planned,
          pipeline_card_id: selectedPipelineCard?.id || null,
        },
      });

      if (result.data && result.data.updateQuickNote) {
        enqueueSnackbar('Quick Note updated successfully!', { variant: 'success' });
        if (onSuccess) {
          onSuccess(result.data.updateQuickNote);
        }
        onClose();
      }
    } catch (error) {
      console.error('Error updating note:', error);
      enqueueSnackbar(
        error.message || 'Failed to update note. Please try again.',
        { variant: 'error' }
      );
    }
  };

  render() {
    const { open, onClose, classes } = this.props;
    const { inventoryUser, categoryIds, activityTypeId, interactionDate, timeSpent, comment, planned, selectedPipelineCard, errors } = this.state;

    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Edit Quick Note</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Query query={GET_FORM_DATA}>
            {({ loading, error, data }) => {
              if (loading) {
                return (
                  <div className={classes.loadingContainer}>
                    <CircularProgress />
                  </div>
                );
              }
              
              if (error) {
                return (
                  <Typography color="error">
                    Error loading form data: {error.message}
                  </Typography>
                );
              }

              const { practiceExchange, successionPlanning, other } = this.processCategories(data?.getQuickNoteCategories);

              return (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <InventoryUserAutocomplete
                      value={inventoryUser}
                      onChange={this.handleInventoryUserChange}
                      isMulti={false}
                      textFieldProps={{
                        label: 'Inventory User',
                        required: true,
                        error: !!errors.inventoryUser,
                        helperText: errors.inventoryUser,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      options={data?.active_deals_for_quick_notes || []}
                      getOptionLabel={(option) => {
                        if (!option) return '';
                        const name = option.name || 'Unnamed';
                        const pipelineName = option.pipeline?.name || 'Unknown Pipeline';
                        const stageName = option.pipeline_stage?.name || 'Unknown Stage';
                        return `${name} - ${pipelineName} (${stageName})`;
                      }}
                      value={selectedPipelineCard}
                      onChange={(e, value) => this.setState({ selectedPipelineCard: value })}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Link to Case/Deal (optional)"
                          helperText="Associate this note with an active deal or case"
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required error={!!errors.categoryIds}>
                      <InputLabel>Categories</InputLabel>
                      <Select
                        multiple
                        name="categoryIds"
                        value={categoryIds}
                        onChange={this.handleCategoryChange}
                        renderValue={(selected) => {
                          const allCategories = [...practiceExchange, ...successionPlanning, ...other];
                          return selected
                            .map(id => {
                              const cat = allCategories.find(c => c.id === id);
                              return cat ? cat.displayName : id;
                            })
                            .join(', ');
                        }}
                      >
                        {practiceExchange.length > 0 && (
                          <ListSubheader disableSticky>Practice Exchange</ListSubheader>
                        )}
                        {practiceExchange.map((category) => (
                          <MenuItem key={category.id} value={category.id}>
                            <Checkbox checked={categoryIds.indexOf(category.id) > -1} />
                            {category.displayName}
                          </MenuItem>
                        ))}
                        
                        {successionPlanning.length > 0 && (
                          <ListSubheader disableSticky>Succession Planning</ListSubheader>
                        )}
                        {successionPlanning.map((category) => (
                          <MenuItem key={category.id} value={category.id}>
                            <Checkbox checked={categoryIds.indexOf(category.id) > -1} />
                            {category.displayName}
                          </MenuItem>
                        ))}
                        
                        {other.length > 0 && (
                          <ListSubheader disableSticky>Other</ListSubheader>
                        )}
                        {other.map((category) => (
                          <MenuItem key={category.id} value={category.id}>
                            <Checkbox checked={categoryIds.indexOf(category.id) > -1} />
                            {category.displayName}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.categoryIds && <Typography color="error" variant="caption">{errors.categoryIds}</Typography>}
                      <FormHelperText>You can select multiple categories</FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required error={!!errors.activityTypeId}>
                      <InputLabel>Activity Type</InputLabel>
                      <Select
                        name="activityTypeId"
                        value={activityTypeId}
                        onChange={this.handleInputChange}
                      >
                        {data?.getActivityTypes.map((act) => (
                          <MenuItem key={act.id} value={act.id}>{act.name}</MenuItem>
                        ))}
                      </Select>
                      {errors.activityTypeId && <Typography color="error" variant="caption">{errors.activityTypeId}</Typography>}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                      <KeyboardDatePicker
                        fullWidth
                        label="Interaction Date"
                        format="yyyy-MM-dd"
                        value={interactionDate}
                        onChange={this.handleDateChange}
                        required
                        error={!!errors.interactionDate}
                        helperText={errors.interactionDate}
                      />
                    </MuiPickersUtilsProvider>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required error={!!errors.timeSpent}>
                      <InputLabel>Time Spent</InputLabel>
                      <Select
                        name="timeSpent"
                        value={timeSpent}
                        onChange={this.handleInputChange}
                      >
                        {TIME_SPENT_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.timeSpent && <Typography color="error" variant="caption">{errors.timeSpent}</Typography>}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      name="comment"
                      label="Notes"
                      value={comment}
                      onChange={this.handleInputChange}
                      required
                      error={!!errors.comment}
                      helperText={errors.comment || `${comment.length}/2000`}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl component="fieldset" error={!!errors.planned}>
                      <Typography variant="subtitle1" gutterBottom>
                        Type
                      </Typography>
                      <RadioGroup
                        row
                        value={planned}
                        onChange={(e) => this.setState({ planned: e.target.value === 'true' })}
                      >
                        <FormControlLabel
                          value={true}
                          control={<Radio color="primary" />}
                          label="Planned"
                        />
                        <FormControlLabel
                          value={false}
                          control={<Radio color="primary" />}
                          label="Unplanned"
                        />
                      </RadioGroup>
                      {errors.planned && (
                        <FormHelperText>{errors.planned}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
              );
            }}
          </Query>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Cancel
          </Button>
          <Mutation mutation={UPDATE_QUICK_NOTE}>
            {(updateMutation, { loading }) => (
              <Button 
                onClick={() => this.handleSubmit(updateMutation)} 
                color="primary" 
                variant="contained" 
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Update Note'}
              </Button>
            )}
          </Mutation>
        </DialogActions>
      </Dialog>
    );
  }
}

EditQuickNoteDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  note: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  enqueueSnackbar: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

EditQuickNoteDialog.defaultProps = {
  onSuccess: null,
};

export default withStyles(styles)(withSnackbar(EditQuickNoteDialog));

